import json from '#shared/util/json.js';

/**
 * @callback WebSocketClientSendMethod
 * @param {string} path Request path
 * @param {object} payload Request payload
 * @returns {Promise<object>}
 */

/**
 * @typedef {object} WebSocketWrappedClient
 * @property {WebSocketClientSendMethod} send
 */

/** @type {WebSocket} */
let ws;

/**
 * Create WebSocket connection on the provided url
 * @param {string} [url] Url to the websocket server (defaults to location.host)
 * @returns {WebSocketWrappedClient} Created WebSocket client
 */
const createSocketClient = (url) => {
	// Helper to check for the expected status of the WebSocket client
	const waitForStatus = (status) => new Promise((connected) => {
		const checkConnection = () => {
			if (ws instanceof WebSocket && ws.readyState === status) {
				connected();
			} else {
				setTimeout(() => checkConnection(), 500);
			}
		};
		checkConnection();
	});
	// Create connection to the server if not already connected
	const createConnection = async () => {
		// If WebSocket client was not yet initialised or is in closed state, create connection
		if (!ws || ws instanceof WebSocket && ws.readyState === WebSocket.CLOSED) {
			ws = new WebSocket(url ?? `wss://${location.host}`);
		}
		// If WebSocket client is connecting, wait for the connection
		if (ws instanceof WebSocket && ws.readyState === WebSocket.CONNECTING) {
			await waitForStatus(WebSocket.OPEN);
		}
		// If WebSocket client is closing, terminate the connection (immediate)
		if (ws instanceof WebSocket && ws.readyState === WebSocket.CLOSING) {
			await waitForStatus(WebSocket.CLOSED);
		}
	};
	// Return required methods
	return {
		send: (path, payload = null, calledOnce = true) => new Promise((respond) => {
			createConnection().then(() => {
				const messageId = String(Date.now());
				ws.addEventListener('message', (response) => {
					const responseObject = json.parse(response.data) ?? {};
					const status = responseObject.data.status;
					if (responseObject.path === path && status === 'OK' || status === 'ERROR' && responseObject.id === messageId) {
						respond(responseObject.data);
					}
				}, {
					once: calledOnce
				});
				ws.send(json.stringify({
					id: messageId,
					path,
					payload
				}));
			});
		})
	};
};

export default createSocketClient;
